import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { viagensService } from '../../services/viagens';
import { veiculosService } from '../../services/veiculos';
import { motoristasService } from '../../services/motoristas';
import { ParadaDaViagem, Viagem } from '../../types';
import CurrencyInput from '../../components/CurrencyInput';
import { KmInput } from '../../components/MaskedInputs';
import { formatDate, formatKilometers } from '../../utils/formatters';
import CampoDeLugar from '../../components/CampoDeLugar';
import { ChevronDown, ChevronUp, Loader2, Plus, X } from 'lucide-react';
import SearchableSelect from '../../components/SearchableSelect';
import DicaUltimoKm from '../../components/DicaUltimoKm';
import { erroDeKmMenor, useUltimoKm } from '../../hooks/useUltimoKm';

// Listas de seleção precisam vir completas: o SearchableSelect filtra em memória,
// então o que não veio na resposta é invisível para a busca do usuário.
const OPCOES_SELECT = { per_page: 500 } as const;

// O mesmo teto do servidor: a ultima linha e o destino final, as outras sao
// as paradas — ate dez.
const MAX_DESTINOS = 11;

interface Destino {
  // Identidade da linha, fixa enquanto ela existir. Com o indice como `key`,
  // mover ou remover um destino trocaria o texto dentro de cada campo, e o
  // campo leria isso como digitacao: uma consulta cobrada ao mapa por linha.
  chave: number;
  endereco: string;
  place_id: string;
}

let ultimaChave = 0;
const novoDestino = (endereco = '', place_id = ''): Destino => ({ chave: ++ultimaChave, endereco, place_id });

const ViagensForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEditing = Boolean(id);

  const [distancia, setDistancia] = useState<number | null>(null);
  const [calculandoDistancia, setCalculandoDistancia] = useState(false);

  // Os destinos na ordem da viagem. Na API o ultimo vira `destino` e os de
  // antes viram `paradas` — assim a viagem de destino unico continua igual.
  const [destinos, setDestinos] = useState<Destino[]>(() => [novoDestino()]);

  const [formData, setFormData] = useState({
    veiculo_id: '',
    motorista_id: '',
    data_saida: '',
    data_chegada: '',
    km_saida: '',
    km_chegada: '',
    origem: '',
    origem_place_id: '',
    rota: '',
    tipo: 'comercial',
    categoria: 'aleatoria',
    quantidade_dias: '5',
    finalidade: '',
    descricao: '',
    status: 'planejada',
    valor_combustivel: '',
    valor_pedagio: '',
    valor_outros: '',
    numero_passageiros: '',
    observacoes: ''
  });

  // Buscar viagem para edição
  const { data: viagemData } = useQuery({
    queryKey: ['viagem', id],
    queryFn: () => viagensService.getViagem(Number(id)),
    enabled: isEditing
  });

  // Buscar veículos
  const { data: veiculosData } = useQuery({
    queryKey: ['veiculos', OPCOES_SELECT],
    queryFn: () => veiculosService.getVeiculos(OPCOES_SELECT)
  });

  // Buscar motoristas
  const { data: motoristasData } = useQuery({
    queryKey: ['motoristas', OPCOES_SELECT],
    queryFn: () => motoristasService.getMotoristas(OPCOES_SELECT)
  });

  // Mutation para criar/atualizar
  const mutation = useMutation({
    mutationFn: (data: Partial<Viagem>) => {
      if (isEditing) {
        return viagensService.updateViagem(Number(id), data);
      } else {
        return viagensService.createViagem(data);
      }
    },
    onSuccess: (resposta: any) => {
        // Invalidar so a lista deixava o detalhe e o proprio formulario
        // servindo o cache anterior a edicao: o registro aparecia atualizado na
        // listagem e desatualizado ao abrir. Sao chaves diferentes, e as duas
        // precisam cair.
      queryClient.invalidateQueries({ queryKey: ['viagens'] });
      if (id) {
        queryClient.invalidateQueries({ queryKey: ['viagem', id] });
      }
      // Quem cadastrou 15 dias precisa saber que sairam 15 viagens, e nao uma.
      const criadas = resposta?.total_criadas ?? 1;
      toast.success(
        isEditing
          ? 'Viagem atualizada com sucesso!'
          : criadas > 1
            ? `Viagem fixa criada: ${criadas} viagens, uma por dia.`
            : 'Viagem criada com sucesso!',
      );
      navigate('/viagens');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao salvar viagem');
    }
  });

  // Preencher formulário quando editando
  useEffect(() => {
    if (viagemData?.data) {
      const viagem = viagemData.data;
      setFormData({
        veiculo_id: viagem.veiculo_id?.toString() ?? '',
        motorista_id: viagem.motorista_id?.toString() ?? '',
        data_saida: viagem.data_saida ? new Date(viagem.data_saida).toISOString().slice(0, 16) : '',
        data_chegada: viagem.data_chegada ? new Date(viagem.data_chegada).toISOString().slice(0, 16) : '',
        km_saida: viagem.km_saida?.toString() ?? '',
        km_chegada: viagem.km_chegada?.toString() ?? '',
        origem: viagem.origem ?? '',
        origem_place_id: viagem.origem_place_id ?? '',
        rota: viagem.rota ?? '',
        tipo: viagem.tipo ?? 'comercial',
        // A serie ja existe quando se edita: cada viagem dela e uma viagem
        // comum, e mudar a quantidade de dias aqui nao criaria as outras.
        categoria: viagem.categoria ?? 'aleatoria',
        quantidade_dias: '5',
        finalidade: viagem.finalidade ?? '',
        descricao: viagem.descricao ?? '',
        status: viagem.status ?? 'planejada',
        valor_combustivel: viagem.valor_combustivel?.toString() ?? '',
        valor_pedagio: viagem.valor_pedagio?.toString() ?? '',
        valor_outros: viagem.valor_outros?.toString() ?? '',
        numero_passageiros: viagem.numero_passageiros?.toString() ?? '',
        observacoes: viagem.observacoes ?? ''
      });
      setDestinos([
        ...(viagem.paradas ?? []).map((parada) => novoDestino(parada.endereco, parada.place_id ?? '')),
        novoDestino(viagem.destino ?? '', viagem.destino_place_id ?? ''),
      ]);
    }
  }, [viagemData]);

  const alterarDestino = (indice: number, endereco: string, placeId: string | null) =>
    setDestinos((anteriores) =>
      anteriores.map((d, i) => (i === indice ? { ...d, endereco, place_id: placeId ?? '' } : d)),
    );

  const adicionarDestino = () => setDestinos((anteriores) => [...anteriores, novoDestino()]);

  const removerDestino = (indice: number) =>
    setDestinos((anteriores) => anteriores.filter((_, i) => i !== indice));

  // Troca o destino com o vizinho. Botao, e nao arrastar: funciona no
  // celular e no teclado, e a lista e curta.
  const moverDestino = (indice: number, direcao: -1 | 1) =>
    setDestinos((anteriores) => {
      const alvo = indice + direcao;
      if (alvo < 0 || alvo >= anteriores.length) return anteriores;
      const novos = [...anteriores];
      [novos[indice], novos[alvo]] = [novos[alvo], novos[indice]];
      return novos;
    });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // O km nao vem preenchido aqui de proposito: a saida ainda vai acontecer, e
  // quem le o painel na hora e o motorista, pelo portal. Preencher agora seria
  // repetir o chute que gerou uma viagem saindo com 200.000 km num veiculo em
  // 103.432. A gestao ve o piso e nao consegue digitar abaixo dele.
  const { ultimo: ultimoKm, carregando: buscandoKm } = useUltimoKm(formData.veiculo_id);
  const erroKmSaida = erroDeKmMenor(formData.km_saida, ultimoKm);
  const erroKmChegada = erroDeKmMenor(formData.km_chegada, ultimoKm);

  // Chegada prevista igual ou anterior a saida: o servidor recusa com a mesma
  // frase; aqui o aviso aparece enquanto se digita, no proprio campo.
  const erroChegada =
    formData.data_chegada && formData.data_saida && formData.data_chegada <= formData.data_saida
      ? 'A chegada prevista precisa ser depois da saída. Pode ser no mesmo dia, mas não no mesmo horário.'
      : null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (erroChegada) {
      toast.error(erroChegada);
      return;
    }

    if (destinos.some((d) => !d.endereco.trim())) {
      toast.error('Preencha todos os destinos ou remova o que ficou em branco.');
      return;
    }

    if (!isEditing && (erroKmSaida || erroKmChegada)) {
      toast.error(erroKmSaida ?? erroKmChegada ?? '');
      return;
    }

    const paradas = destinos.slice(0, -1);
    const final = destinos[destinos.length - 1];

    // Converter dados para o formato esperado pela API
    const submitData: Partial<Viagem> = {
      veiculo_id: formData.veiculo_id ? Number(formData.veiculo_id) : undefined,
      motorista_id: formData.motorista_id ? Number(formData.motorista_id) : undefined,
      data_saida: formData.data_saida,
      // `null`, e nao `undefined`: campo apagado precisa chegar a API para ser
      // apagado. Com undefined ele nem era enviado — a tela dizia "salvo" e a
      // data de chegada que o gestor tinha limpado continuava la.
      data_chegada: formData.data_chegada || null,
      km_saida: formData.km_saida ? Number(formData.km_saida) : null,
      km_chegada: formData.km_chegada ? Number(formData.km_chegada) : null,
      origem: formData.origem,
      // O `place_id` vai; a distancia nao. Quem calcula e o servidor, pelo mesmo
      // motivo que o valor do abastecimento saiu do cliente: numero que o
      // formulario manda e numero que alguem pode trocar.
      // `null`, e nao `undefined`, para que trocar um lugar da lista por um
      // digitado a mao apague o place_id antigo na edicao.
      origem_place_id: formData.origem_place_id || null,
      destino: final.endereco,
      destino_place_id: final.place_id || null,
      // Lista vazia, e nao ausente: remover a ultima parada na edicao precisa
      // chegar ao servidor.
      paradas: paradas.map((p): ParadaDaViagem => ({ endereco: p.endereco, place_id: p.place_id || null })),
      rota: formData.rota || undefined,
      tipo: formData.tipo as any,
      categoria: formData.categoria as any,
      finalidade: formData.finalidade,
      descricao: formData.descricao || undefined,
      status: formData.status as any,
      valor_combustivel: formData.valor_combustivel ? Number(formData.valor_combustivel) : undefined,
      valor_pedagio: formData.valor_pedagio ? Number(formData.valor_pedagio) : undefined,
      valor_outros: formData.valor_outros ? Number(formData.valor_outros) : undefined,
      numero_passageiros: formData.numero_passageiros ? Number(formData.numero_passageiros) : undefined,
      observacoes: formData.observacoes || undefined,
    };

    // So a viagem fixa se repete, e so na criacao: editar uma viagem da serie
    // edita aquele dia, nao a serie.
    if (!isEditing && formData.categoria === 'fixa') {
      (submitData as any).quantidade_dias = Number(formData.quantidade_dias);
    }

    mutation.mutate(submitData);
  };

  const veiculos = veiculosData?.data || [];
  const motoristas = motoristasData?.data || [];

  /**
   * O periodo por extenso, antes de gravar.
   *
   * "5 dias" nao diz em que dia termina, e e justamente ai que o erro aparece:
   * quem quer ate sexta digita o numero errado e so descobre com a serie ja no
   * banco, para apagar uma por uma.
   */
  /**
   * A distancia da rota, buscada quando todos os lugares vieram da lista.
   *
   * So para o gestor ver antes de salvar: o numero que fica gravado e calculado
   * de novo no servidor, que e quem manda. A segunda consulta acerta o cache e
   * nao custa nada.
   */
  // Uma string so como dependencia do efeito: o array muda de identidade a
  // cada tecla digitada, os place_ids nao.
  const placeIdsDosDestinos = destinos.map((d) => d.place_id).join('|');

  useEffect(() => {
    const ids = placeIdsDosDestinos.split('|');

    // Um lugar digitado a mao, sem place_id, e a rota ficaria mais curta que a
    // de verdade. Melhor nao mostrar numero.
    if (!formData.origem_place_id || ids.some((id) => !id)) {
      setDistancia(null);
      return;
    }

    let cancelado = false;
    setCalculandoDistancia(true);

    viagensService
      .calcularDistancia(formData.origem_place_id, ids[ids.length - 1], ids.slice(0, -1))
      .then((resposta) => {
        if (!cancelado) setDistancia(resposta.data?.distancia_km ?? null);
      })
      .catch(() => {
        // Sem distancia o cadastro continua inteiro: e informacao a mais, nao o
        // servico.
        if (!cancelado) setDistancia(null);
      })
      .finally(() => {
        if (!cancelado) setCalculandoDistancia(false);
      });

    return () => {
      cancelado = true;
    };
  }, [formData.origem_place_id, placeIdsDosDestinos]);

  // Serie fixa sendo criada agora: e o unico caso em que o formulario cadastra
  // varias viagens de uma vez, e o que muda o comportamento de outros campos.
  const serieFixa = !isEditing && formData.categoria === 'fixa';

  const resumoDaSerie = (() => {
    const dias = Number(formData.quantidade_dias);
    if (!formData.data_saida || !Number.isFinite(dias) || dias < 1) {
      return 'De 1 a 60 dias. Cada dia vira uma viagem.';
    }

    const inicio = new Date(formData.data_saida);
    if (Number.isNaN(inicio.getTime())) {
      return 'De 1 a 60 dias. Cada dia vira uma viagem.';
    }

    const fim = new Date(inicio);
    fim.setDate(fim.getDate() + dias - 1);

    return `${dias} viagem(ns): de ${formatDate(inicio.toISOString())} até ${formatDate(fim.toISOString())}.`;
  })();

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Editar viagem' : 'Nova viagem'}
        </h1>
        <p className="text-gray-600">
          {isEditing ? 'Atualize as informações da viagem' : 'Preencha os dados da nova viagem'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Informações Básicas</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SearchableSelect
              label="Veículo"
              required
              placeholder="Selecione um veículo"
              value={formData.veiculo_id}
              onChange={(v) => setFormData(prev => ({ ...prev, veiculo_id: v }))}
              options={veiculos.map((veiculo) => ({
                value: String(veiculo.id),
                label: `${veiculo.placa} - ${veiculo.marca} ${veiculo.modelo}`,
              }))}
            />

            <SearchableSelect
              label="Motorista"
              required
              placeholder="Selecione um motorista"
              value={formData.motorista_id}
              onChange={(v) => setFormData(prev => ({ ...prev, motorista_id: v }))}
              options={motoristas.map((motorista) => ({
                value: String(motorista.id),
                label: motorista.nome,
              }))}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data e Hora de Saída *
              </label>
              <input
                type="datetime-local"
                name="data_saida"
                value={formData.data_saida}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data e Hora de Chegada
              </label>
              <input
                type="datetime-local"
                name="data_chegada"
                value={formData.data_chegada}
                onChange={handleInputChange}
                min={formData.data_saida || undefined}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                  erroChegada ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
              />
              {erroChegada ? (
                <p className="mt-1 text-xs text-red-600">{erroChegada}</p>
              ) : (
                <p className="mt-1 text-xs text-gray-500">
                  Previsão, opcional. Pode ser no mesmo dia da saída, mas não no mesmo horário. Não impede o
                  motorista de finalizar — a chegada real é registrada por ele no portal.
                </p>
              )}
            </div>

            {/* Quem le o hodometro e o motorista, na hora de sair: ele informa
                os dois pelo portal. Os campos ficam aqui, e opcionais, porque o
                gestor e quem corrige quando o motorista erra — ele nao pode
                editar o proprio lancamento. */}
            <div>
              <KmInput
                label="KM de Saída"
                name="km_saida"
                value={formData.km_saida}
                onChange={handleInputChange}
              />
              <DicaUltimoKm ultimo={ultimoKm} erro={isEditing ? null : erroKmSaida} carregando={buscandoKm} />
            </div>

            <div>
              <KmInput
                label="KM de Chegada"
                name="km_chegada"
                value={formData.km_chegada}
                onChange={handleInputChange}
              />
              <DicaUltimoKm ultimo={null} erro={isEditing ? null : erroKmChegada} />
            </div>

            <p className="md:col-span-2 text-sm text-gray-500">
              A quilometragem é informada pelo motorista no portal dele, ao sair e ao chegar. Preencha aqui
              apenas se precisar corrigir.
            </p>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Rota e destino</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <CampoDeLugar
              label="Origem"
              name="origem"
              value={formData.origem}
              required
              placeholder="Ex: São Paulo - SP"
              onChange={(texto, placeId) =>
                setFormData((anterior) => ({ ...anterior, origem: texto, origem_place_id: placeId ?? '' }))
              }
            />

            {/* Os destinos em ordem. O ultimo e onde a viagem termina; os
                outros sao paradas no caminho. */}
            <div className="md:col-span-2 space-y-3">
              {destinos.map((destino, indice) => (
                <div key={destino.chave} className="flex items-end gap-2">
                  <div className="flex-1 min-w-0">
                    <CampoDeLugar
                      label={destinos.length > 1 ? `Destino ${indice + 1}` : 'Destino'}
                      name={indice === 0 ? 'destino' : `destino_${indice + 1}`}
                      value={destino.endereco}
                      required
                      placeholder="Ex: Campinas - SP"
                      onChange={(texto, placeId) => alterarDestino(indice, texto, placeId)}
                    />
                  </div>
                  {destinos.length > 1 && (
                    <div className="mb-0.5 flex">
                      <button
                        type="button"
                        onClick={() => moverDestino(indice, -1)}
                        disabled={indice === 0}
                        className="p-2 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
                        aria-label={`Subir destino ${indice + 1}`}
                        title="Subir"
                      >
                        <ChevronUp className="w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moverDestino(indice, 1)}
                        disabled={indice === destinos.length - 1}
                        className="p-2 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
                        aria-label={`Descer destino ${indice + 1}`}
                        title="Descer"
                      >
                        <ChevronDown className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                  {destinos.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removerDestino(indice)}
                      className="mb-0.5 p-2 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50"
                      aria-label={`Remover destino ${indice + 1}`}
                      title="Remover destino"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}

              <div className="flex flex-wrap items-center justify-between gap-2">
                {destinos.length < MAX_DESTINOS ? (
                  <button
                    type="button"
                    onClick={adicionarDestino}
                    className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    <Plus className="w-4 h-4" /> Adicionar destino
                  </button>
                ) : (
                  <span className="text-sm text-gray-500">Limite de {MAX_DESTINOS} destinos por viagem.</span>
                )}
                {destinos.length > 1 && (
                  <span className="text-xs text-gray-500">
                    A viagem passa pelos destinos nesta ordem e termina no último.
                  </span>
                )}
              </div>
            </div>

            {/* A distancia aparece so quando os dois lugares vieram da lista: e
                o `place_id` que permite calcular. Endereco digitado a mao
                continua valendo, e o campo fica em branco. */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Distância prevista
              </label>
              <div className="mt-1 flex items-center gap-2 rounded-md bg-gray-50 border border-gray-200 px-3 py-2">
                {calculandoDistancia ? (
                  <span className="text-sm text-gray-500 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Calculando pelo mapa...
                  </span>
                ) : distancia !== null ? (
                  <span className="text-sm font-semibold text-gray-900">
                    {formatKilometers(distancia)}
                  </span>
                ) : (
                  <span className="text-sm text-gray-500">
                    Escolha a origem e todos os destinos na lista de sugestões para calcular.
                  </span>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                É a rota que o mapa traça. O quilômetro rodado de verdade vem do painel, informado pelo
                motorista — e comparar os dois é o que mostra desvio de trajeto.
              </p>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rota
              </label>
              <input
                type="text"
                name="rota"
                value={formData.rota}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Ex: SP-348 (Bandeirantes)"
              />
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Finalidade e tipo</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo *
              </label>
              <select
                name="tipo"
                value={formData.tipo}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="comercial">Comercial</option>
                <option value="particular">Particular</option>
                <option value="manutencao">Manutenção</option>
                <option value="emergencia">Emergência</option>
                <option value="outros">Outros</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoria *
              </label>
              <select
                name="categoria"
                value={formData.categoria}
                onChange={handleInputChange}
                required
                disabled={isEditing}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md disabled:bg-gray-100"
              >
                <option value="aleatoria">Aleatória (uma viagem)</option>
                <option value="fixa">Fixa (repete todo dia)</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {isEditing
                  ? 'A série já foi criada. Aqui você edita apenas este dia.'
                  : 'Fixa é a rota de rotina: cadastra o período inteiro de uma vez.'}
              </p>
            </div>

            {/* So aparece na fixa, e so na criacao: editar uma viagem da serie
                edita aquele dia, e mudar o numero aqui nao criaria as outras. */}
            {serieFixa && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="quantidade_dias">
                  Quantidade de dias *
                </label>
                <input
                  id="quantidade_dias"
                  type="number"
                  name="quantidade_dias"
                  min={1}
                  max={60}
                  value={formData.quantidade_dias}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {resumoDaSerie}
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              {/* Na serie fixa o status fica travado em planejada: os dias sao
                  futuros, e um dia futuro nao pode estar em andamento. O
                  servidor forca de qualquer jeito — travar aqui evita o gestor
                  escolher uma coisa e receber outra sem entender por que. */}
              <select
                name="status"
                value={serieFixa ? 'planejada' : formData.status}
                onChange={handleInputChange}
                disabled={serieFixa}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md disabled:bg-gray-100"
              >
                <option value="planejada">Planejada</option>
                <option value="em_andamento">Em Andamento</option>
                <option value="concluida">Concluída</option>
                <option value="cancelada">Cancelada</option>
              </select>
              {serieFixa && (
                <p className="mt-1 text-xs text-gray-500">
                  A agenda nasce planejada. Quem inicia cada dia é o motorista, pelo portal, informando a
                  quilometragem do painel.
                </p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Finalidade *
              </label>
              <input
                type="text"
                name="finalidade"
                value={formData.finalidade}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Ex: Entrega de mercadorias"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrição
              </label>
              <textarea
                name="descricao"
                value={formData.descricao}
                onChange={handleInputChange}
                rows={3}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Descrição detalhada da viagem..."
              />
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Custos e informações adicionais</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <CurrencyInput
              name="valor_combustivel"
              value={formData.valor_combustivel}
              onChange={handleInputChange}
              label="Valor Combustível"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />

            <CurrencyInput
              name="valor_pedagio"
              value={formData.valor_pedagio}
              onChange={handleInputChange}
              label="Valor Pedágio"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />

            <CurrencyInput
              name="valor_outros"
              value={formData.valor_outros}
              onChange={handleInputChange}
              label="Outros Custos"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Número de Passageiros
              </label>
              <input
                type="number"
                min="0"
                name="numero_passageiros"
                value={formData.numero_passageiros}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Observações
            </label>
            <textarea
              name="observacoes"
              value={formData.observacoes}
              onChange={handleInputChange}
              rows={3}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Observações adicionais..."
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/viagens')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {mutation.isPending ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Criar')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ViagensForm;
